import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, postSortHandler, useCellChangeHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { useKSK2022_R01Store } from "../Store_KSK2022_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { KSK2022_R01InspectDto } from "../Dto_KSK2022_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useDescText } from "src/hooks/useDescText";
import { DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";
import { showWarning } from "src/utils/toast/toastUtil";

const COLUMN_STATE_KEY = 'KSK2022_R01-inspect-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['badCaseNm'],
	insertedDisabledCells: ['badCaseNm'],
	commonDisabledCells: ['badCaseNm'],
	
	pointerCells: [''],
	insertedPointerCells: ['badQty'],
	commonPointerCells: ['badQty'],

	alignLeftCells: ['badCaseNm'],
};

// inspectNo가 없는 신규 불량사유 행에서만 수량을 입력할 수 있다.
const isInspectQtyEditable = (data?: KSK2022_R01InspectDto | null) => !data?.inspectNo;

interface Props {
	gridRef: React.RefObject<AgGridReact<KSK2022_R01InspectDto> | null>;
	defaultKSK2022_R01InspectRow: KSK2022_R01InspectDto;
}

export const InspectTable_KSK2022_R01 = (props: Props) => {

	const { gridRef, defaultKSK2022_R01InspectRow } = props;

	const { 
			editKSK2022_R01,
			KSK2022_R01InspectList, setKSK2022_R01InspectList, 
			selectedKSK2022_R01InspectList, initSelectedKSK2022_R01InspectList,
			deletedKSK2022_R01InspectList,
			updateKSK2022_R01InspectList,
			inspectColumnState, setInspectColumnState,
			originalKSK2022_R01InspectList,
		} = useKSK2022_R01Store();
	const { renderDescText } = useDescText();

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: KSK2022_R01InspectList,
		defaultGridData: defaultKSK2022_R01InspectRow,
		selectedRow: selectedKSK2022_R01InspectList,
		originalGridData: originalKSK2022_R01InspectList,
		updateRow: (index: number, data: any) => updateKSK2022_R01InspectList(index, data),
	});

	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		badCaseNm: renderDescText(DESC_FLD.DEFECT_REASON, "불량사유"),
		badQty: renderDescText(DESC_FLD.DEFECT_QTY, "불량수량"),
	}), [renderDescText]);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK2022_R01InspectDto>({
			idKey: 'valueIndex',
			selectedList: selectedKSK2022_R01InspectList,
			deletedList: deletedKSK2022_R01InspectList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});

		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedKSK2022_R01InspectList, deletedKSK2022_R01InspectList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	const commonInputColumns = ['badQty'];
	const defaultEditableColumns = ['badQty'];

	const baseColumnDefs: ColDef<KSK2022_R01InspectDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
        {
			headerName: renderDescText(DESC_FLD.DEFECT_REASON, "불량사유"),
			colId: 'badCaseNm',
			field: 'badCaseNm',
			Width: 130,
			editable: false,
		},
        {
			headerName: renderDescText(DESC_FLD.DEFECT_QTY, "불량수량"),
			colId: 'badQty',
			field: 'badQty',
			width: 100,
			editable: params => isInspectQtyEditable(params.data),
			pasteable: true,
			cellEditor: 'agNumberCellEditor',
			cellEditorParams: { min: 0 },
            // valueParser는 사용자가 입력한 값을 저장 가능한 숫자로 변환합니다.빈 값이나 숫자가 아닌 값은 0,음수도 0
			valueParser: params => {
				const qty = Number(params.newValue);
				return Number.isFinite(qty) && qty > 0 ? qty : 0;
			},
            // valueFormatter는 값을 변경하지 않고 화면 표시만 조정합니다. 행의 0은 빈칸으로 표시, 수량이 입력된 행만 숫자를 표시
			valueFormatter: params => {
				if (params.node?.rowPinned === 'bottom') return String(Number(params.value) || 0);
				return Number(params.value) > 0 ? String(params.value) : '';
			},
		},
		// ...IndexParameterCell(),
	], [renderDescText]);

	const columnDefs: ColDef<KSK2022_R01InspectDto>[] = useMemo(() => {
		if (inspectColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<KSK2022_R01InspectDto>({
			baseColumnDefs,
			columnState: inspectColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, inspectColumnState, headerNames]);

	
	// 신규 행의 입력값을 0 이상으로 보정하고 저장 대상 여부를 갱신한다.
	const getKSK2022_R01InspectRowBeforeUpdate = useCallback((
		updatedData: KSK2022_R01InspectDto,
		field: string,
		newValue: unknown,
	): KSK2022_R01InspectDto => {
		if (field !== 'badQty') return updatedData;
		if (!isInspectQtyEditable(updatedData)) {
			const original = originalKSK2022_R01InspectList.find(
				item => item.valueIndex === updatedData.valueIndex,
			);
			return {
				...updatedData,
				badQty: original?.badQty ?? updatedData.badQty,
				isModified: false,
			};
		}

		const qty = Math.max(0, Number(newValue) || 0);
		const totalBadQty = Math.max(0, Number(editKSK2022_R01?.badQty) || 0);
		const validQty = Math.min(qty, totalBadQty);
		if (qty > totalBadQty) {
			showWarning(`불량사유별 수량은 불량수량(${totalBadQty})을 초과할 수 없습니다.`);
		}
		return {
			...updatedData,
			badQty: validQty,
			inserted: !updatedData.inspectNo && validQty > 0,
		};
	}, [editKSK2022_R01?.badQty, originalKSK2022_R01InspectList]);

	const handleCellChange = useCellChangeHandler({
		dataList: KSK2022_R01InspectList,
		originalDataList: originalKSK2022_R01InspectList,
		updateRow: updateKSK2022_R01InspectList,
		customBeforeUpdate: getKSK2022_R01InspectRowBeforeUpdate,
	});

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2022_R01InspectList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<KSK2022_R01InspectDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return isInspectQtyEditable(data) && commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(KSK2022_R01InspectList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2022_R01InspectDto>[] = useMemo(() => [
	], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2022_R01InspectList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<KSK2022_R01InspectDto>) => {
		postSortHandler(params, KSK2022_R01InspectList, originalKSK2022_R01InspectList, setKSK2022_R01InspectList);
	}, [KSK2022_R01InspectList, originalKSK2022_R01InspectList, ]);

	const pinnedBottomRowData = useMemo(() => {

		// 하단 고정 행에는 수량이 입력된 사유 개수와 전체 불량수량을 표시한다.
		const totalQty : number = KSK2022_R01InspectList
			.reduce((sum, item) => sum + Math.max(0, Number(item.badQty) || 0), 0);
		const selectedReasonCount = KSK2022_R01InspectList
			.filter(item => Number(item.badQty) > 0)
			.length;

		return [{
			badCaseNm: formatDescCount(
				selectedReasonCount,
				renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건"),
			),
			badQty: totalQty,
		}];	  
	}, [KSK2022_R01InspectList, renderDescText]);

	return (
		<>
			{/* selectedDetailKSK2022_R01 : {JSON.stringify(selectedKSK2022_R01InspectList)}<br /> */}
			
			<GridContextWrapper<KSK2022_R01InspectDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setKSK2022_R01InspectList}
				initSelected={initSelectedKSK2022_R01InspectList}
				defaultRowData={defaultKSK2022_R01InspectRow}
				baseColumnDefs={columnDefs}	
				setColumnState={setInspectColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<KSK2022_R01InspectDto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={gridDefaultColDef}
					overlayNoRowsTemplate={`<span style="padding: 32px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`}

					rowClassRules={createRowClassRules<KSK2022_R01InspectDto>({
						idKey: 'valueIndex',
						selectedList: selectedKSK2022_R01InspectList,
						deletedList: deletedKSK2022_R01InspectList
					})}

					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setInspectColumnState}		//컬럼 상태 저장
				/>
			</GridContextWrapper>
		</>
	)
}
